/**
 * Document versioning service.
 * Handles version checking and history queries.
 */

import { supabase } from "@/integrations/supabase/client";
import type { Document } from "@/types/documents";
import { fromDocumentDB } from "@/types/documents";

export interface VersionCheckResult {
  exists: boolean;
  isDuplicate: boolean;
  existingDocument?: Document;
  currentVersion?: number;
  versionGroup?: string;
}

export interface DocumentVersion {
  id: number;
  version: number;
  /** Elected stored head; version-number order alone does not establish it. */
  is_latest_version: boolean | null;
  filename: string;
  file_path: string;
  size: number;
  content_hash: string | null;
  created_at: string;
  created_by_name?: string;
  /** Processing state — only IFC_PROCESSED versions can be diffed. */
  state: string | null;
  /** Owning building — needed to open the IFC diff viewer for a version pair. */
  building_id: number | null;
}

/**
 * Check if a document with the same identity (project_id + folder_id + filename) exists.
 * Also checks if the content hash matches (true duplicate).
 */
export async function checkExistingVersion(
  projectId: number | null,
  folderId: number | null,
  filename: string,
  contentHash: string | null
): Promise<VersionCheckResult> {
  // Runtime validation to prevent type coercion issues
  if (projectId !== null && typeof projectId !== "number") {
    throw new Error("projectId must be a number or null");
  }
  if (folderId !== null && typeof folderId !== "number") {
    throw new Error("folderId must be a number or null");
  }
  if (typeof filename !== "string" || filename.length === 0) {
    throw new Error("filename must be a non-empty string");
  }

  // Build query for documents at same logical path
  let query = supabase
    .from("documents")
    .select("*")
    .eq("filename", filename)
    .eq("is_deleted", false)
    .order("version", { ascending: false })
    .limit(1);

  // Handle null project_id and folder_id
  if (projectId === null) {
    query = query.is("project_id", null);
  } else {
    query = query.eq("project_id", projectId);
  }

  if (folderId === null) {
    query = query.is("folder_id", null);
  } else {
    query = query.eq("folder_id", folderId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[versionService] Error checking existing version:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return { exists: false, isDuplicate: false };
  }

  const existingDoc = fromDocumentDB(data[0]);

  // Check if content hash matches (true duplicate)
  const isDuplicate =
    contentHash !== null &&
    existingDoc.content_hash !== null &&
    contentHash === existingDoc.content_hash;

  return {
    exists: true,
    isDuplicate,
    existingDocument: existingDoc,
    currentVersion: existingDoc.version ?? 1,
    versionGroup: existingDoc.version_group ?? undefined,
  };
}

/**
 * Get all versions of a document by version_group.
 * Returns versions sorted by version number descending (newest first).
 */
export async function getVersionHistory(
  versionGroup: string,
  options: { requireComplete?: boolean } = {}
): Promise<DocumentVersion[]> {
  const { data, error, count } = await supabase
    .from("documents")
    .select(
      `
      id,
      version,
      is_latest_version,
      filename,
      file_path,
      size,
      content_hash,
      created_at,
      state,
      building_id,
      user_profiles!documents_created_by_fk(first_name, last_name)
    `,
      // Only a caller that refuses a partial history pays for the count.
      options.requireComplete ? { count: "exact" } : undefined
    )
    .eq("version_group", versionGroup)
    .eq("is_deleted", false)
    .order("version", { ascending: false });

  if (error) {
    console.error("[versionService] Error fetching version history:", error);
    throw error;
  }
  // PostgREST stops at max-rows with no error. A reader may show a first
  // page; an editor must not take a partial history for the whole lineage.
  if (options.requireComplete && typeof count === "number" && (data ?? []).length < count) {
    throw new Error(`version history truncated: ${(data ?? []).length} of ${count} rows`);
  }

  return (data || []).map((row: Record<string, unknown>) => {
    const userProfile = row.user_profiles as { first_name?: string; last_name?: string } | null;
    return {
      id: row.id as number,
      version: row.version as number,
      is_latest_version: typeof row.is_latest_version === "boolean" ? row.is_latest_version : null,
      filename: row.filename as string,
      file_path: row.file_path as string,
      size: row.size as number,
      content_hash: row.content_hash as string | null,
      created_at: row.created_at as string,
      created_by_name: userProfile
        ? `${userProfile.first_name || ""} ${userProfile.last_name || ""}`.trim()
        : undefined,
      state: (row.state as string | null) ?? null,
      building_id: (row.building_id as number | null) ?? null,
    };
  });
}

/**
 * Get the latest version of a document by version_group.
 */
export async function getLatestVersion(versionGroup: string): Promise<Document | null> {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("version_group", versionGroup)
    .eq("is_deleted", false)
    .order("version", { ascending: false })
    .limit(1)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      // No rows found
      return null;
    }
    console.error("[versionService] Error fetching latest version:", error);
    throw error;
  }

  return fromDocumentDB(data);
}

/**
 * Count total versions for a document.
 */
export async function getVersionCount(versionGroup: string): Promise<number> {
  const { count, error } = await supabase
    .from("documents")
    .select("*", { count: "exact", head: true })
    .eq("version_group", versionGroup)
    .eq("is_deleted", false);

  if (error) {
    console.error("[versionService] Error counting versions:", error);
    throw error;
  }

  return count ?? 0;
}

/**
 * Get a specific version of a document.
 */
export async function getVersionByNumber(
  versionGroup: string,
  versionNumber: number
): Promise<Document | null> {
  const { data, error } = await supabase
    .from("documents")
    .select("*")
    .eq("version_group", versionGroup)
    .eq("version", versionNumber)
    .eq("is_deleted", false)
    .single();

  if (error) {
    if (error.code === "PGRST116") {
      return null;
    }
    console.error("[versionService] Error fetching version:", error);
    throw error;
  }

  return fromDocumentDB(data);
}

export const versionService = {
  checkExistingVersion,
  getVersionHistory,
  getLatestVersion,
  getVersionCount,
  getVersionByNumber,
};
